import classNames from "classnames";
import { FC, useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { GroupBase } from "react-select";
import FormFieldsContainer from "../../../components/ui/FormFieldsContainer";
import { FormField, Numeric, Select, Switch } from "@rothoblaas/design-system";
import useI18n from "../../../hooks/useTranslations";
import { BeamReinforcementResult, isScrewGeometricallyValid } from "../../../http/beamReinforcement/formulas";
import { Screw } from "../../../http/beamReinforcement/types/screw";
import { ScrewFamily } from "../../../http/beamReinforcement/types/screwFamily";
import { BeamReinforcementConfigurationFormValues } from "../../../http/beamReinforcement/useAutoSaveBeamReinforcementConfiguration";
import styles from "./ScrewCard.module.css";

type ScrewCardProps = {
  defaultValues: BeamReinforcementConfigurationFormValues;
  screws: Screw[];
  result: BeamReinforcementResult | null;
  disabled?: boolean;
};

const ScrewCard: FC<ScrewCardProps> = ({ defaultValues, screws, result, disabled = false }) => {
  const i18n = useI18n();
  const t = i18n.translation.beamReinforcementModule.calculation;
  const r = i18n.translation.beamReinforcementModule.results;
  const {
    control,
    formState: { errors },
  } = useFormContext<BeamReinforcementConfigurationFormValues>();

  const [existingBase, reinforcementBase, reinforcementHeight, reinforcementSides, tipTolerance] = useWatch({
    control,
    name: ["existingBase", "reinforcementBase", "reinforcementHeight", "reinforcementSides", "tipTolerance"],
  });

  // Screws that can't geometrically fit the current beam configuration (length, height,
  // edge distances depend on the existing/reinforcement dimensions entered on the other
  // tabs) stay selectable but are flagged red in the picker instead of being hidden.
  const invalidScrewIds = useMemo(() => {
    if (
      existingBase == null ||
      reinforcementBase == null ||
      reinforcementHeight == null ||
      reinforcementSides == null ||
      tipTolerance == null
    ) {
      return null;
    }

    const ids = new Set<string>();
    screws.forEach((screw) => {
      if (
        !isScrewGeometricallyValid(screw, {
          existingBase,
          reinforcementBase,
          reinforcementHeight,
          reinforcementSides,
          tipTolerance,
        })
      ) {
        ids.add(screw.item.id);
      }
    });
    return ids;
  }, [screws, existingBase, reinforcementBase, reinforcementHeight, reinforcementSides, tipTolerance]);

  const isScrewValid = (screw: Screw): boolean => !invalidScrewIds?.has(screw.item.id);
  const screwLabel = (screw: Screw) =>
    `${screw.screwFamily.name.replace(`${screw.screwFamily.diameter}`, "")} ${screw.screwFamily.diameter}x${screw.length}`;

  const screwGroups: GroupBase<Screw>[] = useMemo(() => {
    const byFamily = new Map<string, { family: ScrewFamily; options: Screw[] }>();
    screws.forEach((screw) => {
      const group = byFamily.get(screw.screwFamily.id) ?? { family: screw.screwFamily, options: [] };
      group.options.push(screw);
      byFamily.set(screw.screwFamily.id, group);
    });

    return Array.from(byFamily.values()).map(({ family, options }) => ({
      label: `${family.name} (Ø ${family.diameter} mm)`,
      options,
    }));
  }, [screws]);

  const allScrewOptions = useMemo(() => screwGroups.flatMap((group) => group.options), [screwGroups]);

  const reasons = result
    ? [
        !result.geomOK ? r.reasonGeometry : null,
        !result.heightOK ? r.reasonHeight : null,
        !result.spacOK ? r.reasonSpacing : null,
        !result.loadOK && result.geomOK ? r.reasonOverload : null,
      ].filter((reason): reason is string => reason !== null)
    : [];

  return (
    <div>
      <FormFieldsContainer fullWidth>
        <Controller
          name="screw"
          control={control}
          defaultValue={defaultValues.screw}
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <FormField label={t.screw} error={fieldState.error}>
              {({ labelId, isInvalid }) => (
                <Select<Screw, false, GroupBase<Screw>>
                  {...field}
                  inputId={labelId}
                  isDisabled={disabled}
                  isInvalid={isInvalid}
                  value={allScrewOptions.find((option) => option.item.id === field.value?.item.id) ?? null}
                  options={screwGroups}
                  getOptionValue={(option) => option.item.id}
                  getOptionLabel={screwLabel}
                  // Invalid screws are listed in red in the menu (the selected value keeps the default color).
                  formatOptionLabel={(option, { context }) => (
                    <span style={context === "menu" && !isScrewValid(option) ? { color: "var(--red)" } : undefined}>
                      {screwLabel(option)}
                    </span>
                  )}
                  onChange={(selectedOption) => field.onChange(selectedOption ?? null)}
                />
              )}
            </FormField>
          )}
        />

        <Controller
          name="screwSpacing"
          control={control}
          defaultValue={defaultValues.screwSpacing}
          rules={{ min: 20 }}
          render={({ field }) => (
            <FormField label={t.screwSpacing} error={errors.screwSpacing}>
              {({ labelId, isInvalid }) => (
                <Numeric
                  {...field}
                  id={labelId}
                  value={field.value}
                  suffix={` ${i18n.translation.common.unitMm}`}
                  decimalScale={0}
                  step={10}
                  min={20}
                  disabled={disabled}
                  allowNegative={false}
                  isInvalid={isInvalid}
                  onValueChange={(value) => field.onChange(value)}
                />
              )}
            </FormField>
          )}
        />

        <Controller
          name="preDrilled"
          control={control}
          defaultValue={defaultValues.preDrilled}
          render={({ field }) => (
            <FormField label={t.preDrilled}>
              {({ labelId }) => (
                <Switch id={labelId} checked={!!field.value} onChange={field.onChange} disabled={disabled} />
              )}
            </FormField>
          )}
        />

        {result && (
          <div className={classNames(styles.state, { [styles.stateBad]: !result.screwOK })}>
            {result.screwOK
              ? `${r.screwAdmissible} · F_v,Rd = ${(result.fvRd / 1000).toFixed(2)} kN · η = ${result.etaConn.toFixed(1)}%`
              : `${r.screwNotAdmissible}: ${reasons.join(" + ")}`}
          </div>
        )}
      </FormFieldsContainer>
    </div>
  );
};

export default ScrewCard;
